// Admin page - content management panel

import { useState, useEffect } from 'react';
import { isAuthenticated, login, logout } from '../lib/auth';

export function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoggedIn(isAuthenticated());
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <main className="pt-24 min-h-screen">
        <div className="container-custom">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
          </div>
        </div>
      </main>
    );
  }

  if (!isLoggedIn) {
    return <LoginPage onLoginSuccess={() => setIsLoggedIn(true)} />;
  }

  return <AdminDashboard onLogout={() => setIsLoggedIn(false)} />;
}

// Login page component
function LoginPage({ onLoginSuccess }: { onLoginSuccess: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(username, password);
      onLoginSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="pt-24 min-h-screen flex items-center justify-center">
      <div className="w-full max-w-md p-8">
        <div className="card">
          <h1 className="font-serif text-2xl font-semibold text-text-primary mb-6 text-center">
            Admin Login
          </h1>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-error/10 border border-error/30 rounded-lg text-error text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full disabled:opacity-50"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}

// Admin dashboard component
function AdminDashboard({ onLogout }: { onLogout: () => void }) {
  const [activeTab, setActiveTab] = useState<
    'profile' | 'experiences' | 'skills' | 'gaps' | 'faqs' | 'instructions'
  >('profile');

  const handleLogout = () => {
    logout();
    onLogout();
  };

  const tabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'experiences', label: 'Experiences' },
    { id: 'skills', label: 'Skills' },
    { id: 'gaps', label: 'Gaps' },
    { id: 'faqs', label: 'FAQs' },
    { id: 'instructions', label: 'AI Instructions' },
  ] as const;

  return (
    <main className="pt-24 min-h-screen">
      <div className="container-custom">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif text-3xl font-semibold text-text-primary">
            Admin Panel
          </h1>
          <button onClick={handleLogout} className="btn-ghost text-sm">
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-border pb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-accent text-white'
                  : 'text-text-secondary hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="card">
          {activeTab === 'profile' && <ProfileEditor />}
          {activeTab === 'experiences' && <ExperiencesEditor />}
          {activeTab === 'skills' && <SkillsEditor />}
          {activeTab === 'gaps' && <GapsEditor />}
          {activeTab === 'faqs' && <FaqsEditor />}
          {activeTab === 'instructions' && <InstructionsEditor />}
        </div>
      </div>
    </main>
  );
}

// Placeholder editor components - these would be fully implemented
function ProfileEditor() {
  return (
    <div className="p-6">
      <h2 className="font-serif text-xl font-semibold mb-4">Edit Profile</h2>
      <p className="text-text-secondary">
        Profile editor will be implemented here. This includes:
      </p>
      <ul className="list-disc list-inside text-text-secondary mt-4 space-y-1">
        <li>Name and title</li>
        <li>Elevator pitch</li>
        <li>Career narrative</li>
        <li>What you're looking for</li>
        <li>What you're NOT looking for</li>
        <li>Availability status</li>
        <li>Social links</li>
      </ul>
    </div>
  );
}

function ExperiencesEditor() {
  return (
    <div className="p-6">
      <h2 className="font-serif text-xl font-semibold mb-4">Edit Experiences</h2>
      <p className="text-text-secondary">
        Experiences editor will be implemented here. For each role, you can edit:
      </p>
      <ul className="list-disc list-inside text-text-secondary mt-4 space-y-1">
        <li>Company name and title</li>
        <li>Date range</li>
        <li>Public bullet points</li>
        <li>Why you joined / left</li>
        <li>What you actually did</li>
        <li>Proudest achievement</li>
        <li>Lessons learned</li>
        <li>What your manager would say</li>
      </ul>
    </div>
  );
}

function SkillsEditor() {
  return (
    <div className="p-6">
      <h2 className="font-serif text-xl font-semibold mb-4">Edit Skills</h2>
      <p className="text-text-secondary">
        Skills editor will be implemented here. For each skill:
      </p>
      <ul className="list-disc list-inside text-text-secondary mt-4 space-y-1">
        <li>Skill name</li>
        <li>Category (Strong / Moderate / Gap)</li>
        <li>Self-rating (1-5)</li>
        <li>Evidence and honest notes</li>
        <li>Years of experience</li>
      </ul>
    </div>
  );
}

function GapsEditor() {
  return (
    <div className="p-6">
      <h2 className="font-serif text-xl font-semibold mb-4">Edit Gaps & Weaknesses</h2>
      <p className="text-text-secondary">
        This is where you document your honest weaknesses. The AI will use this
        to be transparent with employers.
      </p>
      <ul className="list-disc list-inside text-text-secondary mt-4 space-y-1">
        <li>Gap type (skill, experience, environment, role type)</li>
        <li>Description of the gap</li>
        <li>Why it's a gap</li>
        <li>Interest in learning</li>
      </ul>
    </div>
  );
}

function FaqsEditor() {
  return (
    <div className="p-6">
      <h2 className="font-serif text-xl font-semibold mb-4">Edit FAQ Responses</h2>
      <p className="text-text-secondary">
        Pre-written answers to common interview questions:
      </p>
      <ul className="list-disc list-inside text-text-secondary mt-4 space-y-1">
        <li>"Tell me about yourself"</li>
        <li>"What's your biggest weakness?"</li>
        <li>"Why are you leaving your current role?"</li>
        <li>"Tell me about a time you failed"</li>
        <li>Custom Q&A pairs</li>
      </ul>
    </div>
  );
}

function InstructionsEditor() {
  return (
    <div className="p-6">
      <h2 className="font-serif text-xl font-semibold mb-4">Edit AI Instructions</h2>
      <p className="text-text-secondary">
        Configure how the AI represents you. These are anti-sycophancy
        instructions:
      </p>
      <ul className="list-disc list-inside text-text-secondary mt-4 space-y-1">
        <li>"Never oversell me"</li>
        <li>"If the JD asks for X and I don't have it, say so directly"</li>
        <li>"Use phrases like 'I'm probably not your person' when appropriate"</li>
        <li>"Don't hedge - be direct"</li>
        <li>"It's okay to recommend they not hire me"</li>
      </ul>
    </div>
  );
}
